import { MetadataFolder } from "../api";

export interface RestrictedFolderView {
  /** Folders to render in the dropdown. */
  folders: MetadataFolder[];
  /**
   * Prefix to prepend when rebuilding canonical paths from `folders`. Non-empty
   * only when the view is rooted below the top level, so the paths produced here
   * still match the ones held in selected/excluded metadata.
   */
  parentPath: string;
}

/** Resolve a canonical `a/b/c` path against the folder tree. */
function findByExactPath(
  folders: MetadataFolder[],
  targetPath: string,
): MetadataFolder | null {
  let level = folders;
  let node: MetadataFolder | null = null;

  for (const segment of targetPath.split("/")) {
    node = level.find((folder) => folder.folderName === segment) ?? null;
    if (!node) return null;
    level = node.subfolders ?? [];
  }

  return node;
}

/** Legacy fallback: locate a folder at any depth by (fuzzy) name. */
function findByName(
  folders: MetadataFolder[],
  targetName: string,
): MetadataFolder | null {
  for (const folder of folders) {
    if (folder.folderName === targetName) return folder;
    if (folder.folderName.toLowerCase().includes(targetName.toLowerCase())) {
      return folder;
    }
    if (folder.subfolders) {
      const found = findByName(folder.subfolders, targetName);
      if (found) return found;
    }
  }
  return null;
}

/** Keep only branches that are, or lead to, a restricted path. */
function pruneToRestrictions(
  folders: MetadataFolder[],
  restrictions: Set<string>,
  parentPath = "",
): MetadataFolder[] {
  const kept: MetadataFolder[] = [];

  for (const folder of folders) {
    const path = parentPath
      ? `${parentPath}/${folder.folderName}`
      : folder.folderName;

    if (restrictions.has(path)) {
      kept.push(folder);
      continue;
    }

    const subfolders = pruneToRestrictions(
      folder.subfolders ?? [],
      restrictions,
      path,
    );
    if (subfolders.length > 0) {
      kept.push({ ...folder, subfolders });
    }
  }

  return kept;
}

/**
 * Narrow the folder tree to the paths a use case is restricted to. An empty
 * restriction list means the whole library is available.
 */
export function getRestrictedFolderView(
  folders: MetadataFolder[],
  restrictions: readonly string[],
): RestrictedFolderView {
  const targets = restrictions.filter((path) => !!path);

  if (targets.length === 0) {
    return { folders, parentPath: "" };
  }

  // A single restriction renders as that folder's children, with the restricted
  // root implied by the dropdown label rather than shown as a node.
  if (targets.length === 1) {
    const target = targets[0];
    const node =
      findByExactPath(folders, target) ?? findByName(folders, target);
    if (node) {
      const children = node.subfolders ?? [];
      if (children.length > 0) {
        return { folders: children, parentPath: target };
      }
      // Leaf node: show it directly instead of its (empty) children
      const parentPath = target.includes("/")
        ? target.substring(0, target.lastIndexOf("/"))
        : "";
      return { folders: [node], parentPath };
    }
  }

  const pruned = pruneToRestrictions(folders, new Set(targets));
  if (pruned.length > 0) {
    return { folders: pruned, parentPath: "" };
  }

  console.warn(
    `None of the restricted folders [${targets.join(", ")}] were found. Showing all folders.`,
  );
  return { folders, parentPath: "" };
}
